import {
  EMPTY_SKU_FILTER,
  skuFilterMatcher,
  type FilterableSku,
  type SkuFilter,
} from './filter.ts';
import type { SkuMaster } from './sku.ts';

/**
 * 품목이 지금 어느 자리에 있는가, 그리고 그것으로 어떻게 거르는가.
 *
 * 저장하지 않고 늘 계산한다. NEW 는 별도 플래그가 아니라 운영방식이 비어 있는 상태다. 동기화가
 * 우리 소유 필드를 비운 채로 행을 만들고, 사람이 운영방식을 정하는 순간 신규가 아니게 된다.
 */
export type SkuStatus = 'NEW' | 'PLANNED' | 'EXCLUDED' | 'DISCONTINUING' | 'DISCONTINUED';

export const skuStatusOf = (sku: SkuMaster, today: string): SkuStatus => {
  if (sku.operationMode === null) return 'NEW';
  // 주차 표기는 글자 정렬이 곧 시간 순서다.
  if (sku.discontinuedIsoWeek !== null && sku.discontinuedIsoWeek <= today) return 'DISCONTINUED';
  if (sku.discontinuedIsoWeek !== null) return 'DISCONTINUING';
  return sku.planningIncludedOn !== null ? 'PLANNED' : 'EXCLUDED';
};

/**
 * 상태의 이름과 순서. 이 목록 하나가 배지의 글자, 조회 패널의 단추 줄, 정렬 차례를 다 정한다.
 *
 * 손댈 것이 앞이고 끝난 것이 뒤다. 정렬을 저장값에 맡기면 영문 코드 사전순(DISCONTINUED,
 * DISCONTINUING, EXCLUDED, NEW, PLANNED)이 되어 배지의 한글과 아무 관계가 없는 차례가 나온다.
 */
export const SKU_STATUS_NAMES: Readonly<Record<SkuStatus, string>> = {
  NEW: '신규',
  PLANNED: '대상',
  EXCLUDED: '제외',
  DISCONTINUING: '불용예정',
  DISCONTINUED: '불용',
};

const STATUS_RANK = Object.fromEntries(
  (Object.keys(SKU_STATUS_NAMES) as SkuStatus[]).map((status, rank) => [status, rank]),
) as Readonly<Record<SkuStatus, number>>;

export const compareSkuStatus = (a: SkuStatus, b: SkuStatus): number =>
  STATUS_RANK[a] - STATUS_RANK[b];

/** 사람이 손대야 넘어가는 자리. 조회 조건의 첫 항목이 이것을 센다. */
export const needsAttention = (status: SkuStatus): boolean =>
  status === 'NEW' || status === 'DISCONTINUING';

/**
 * 상태로 거르는 축. null 이 전체다.
 *
 * ATTENTION 은 상태가 아니라 여러 상태를 묶은 것이다. 같은 축에 두는 이유는 사람이 이 화면에서
 * 고르는 것이 하나이기 때문이다. 상태 하나를 보든 손댈 것을 보든 같은 질문이다.
 */
export type SkuStatusFilter = 'ATTENTION' | SkuStatus;

/** 조회 패널의 단추 줄. 손댈 것을 맨 앞에 두고 그 뒤는 상태 순서를 그대로 따른다. */
export const SKU_STATUS_FILTER_NAMES: Readonly<Record<SkuStatusFilter, string>> = {
  ATTENTION: '조치 필요',
  ...SKU_STATUS_NAMES,
};

export const matchesStatusFilter = (
  status: SkuStatus,
  filter: SkuStatusFilter | null,
): boolean => {
  if (filter === null) return true;
  return filter === 'ATTENTION' ? needsAttention(status) : status === filter;
};

/**
 * 조회 조건. 품목 축은 두 화면이 함께 쓰는 것을 그대로 받고, 상태만 이 화면이 더한다.
 *
 * 상태를 공통 축에 안 넣는 이유는 판매예측이 그것을 물을 수 없어서다. 시트에 실리는 품목은
 * 계획 대상 조건을 이미 통과했거나 값이 남아 있는 것이라, 신규나 제외 같은 상태가 뜻을 잃는다.
 */
export interface SkuQuery extends SkuFilter {
  readonly status: SkuStatusFilter | null;
}

/** 화면을 열면 손댈 것부터 보인다. 마스터 8 천 행 앞에 사람을 세우지 않는다. */
export const EMPTY_QUERY: SkuQuery = { ...EMPTY_SKU_FILTER, status: 'ATTENTION' };

/**
 * 조회 조건을 술어 하나로 굳힌다. 품목 축은 공통 술어가 보고 상태만 여기서 본다.
 *
 * 품목과 상태를 따로 받는 이유는 둘 다 화면이 미리 구해 둔 것이기 때문이다. 상태는 마스터에서
 * 계산하고 [FilterableSku] 는 마스터에서 옮긴 것이라, 여기서 다시 만들면 마스터 규모에서 키
 * 입력 하나에 같은 일을 수만 번 한다.
 */
export const skuMatcher = (
  query: SkuQuery,
): ((sku: FilterableSku, status: SkuStatus) => boolean) => {
  const matchesFilter = skuFilterMatcher(query);
  return (sku, status) => matchesStatusFilter(status, query.status) && matchesFilter(sku);
};

/**
 * 주소가 품목 하나를 짚어 보낼 때 쓰는 파라미터. 값은 ERP 품목코드다.
 *
 * 다른 화면이 "이 품목을 고쳐야 한다" 고 말할 때 쓴다. 자재 소요량의 결손 목록이 그 자리이며,
 * 그 목록은 품목코드만 알고 우리 id 는 모른다.
 */
export const SKU_PARAM = 'sku';
